import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  ClickAwayListener,
  Popper,
} from "@material-ui/core";
import moment from "moment-timezone";

// Comprehensive timezone data with major timezones and their daylight savings info
const TIMEZONE_DATA = [
  // UTC/GMT
  { value: "UTC", label: "(UTC+0:00) Coordinated Universal Time", offset: 0 },
  { value: "GMT", label: "(GMT+0:00) Dublin, Edinburgh, Lisbon, London", offset: 0 },
  
  // Europe
  { value: "Europe/London", label: "(GMT+0:00/+1:00) London", offset: 0, dstOffset: 1 },
  { value: "Europe/Dublin", label: "(GMT+0:00/+1:00) Dublin", offset: 0, dstOffset: 1 },
  { value: "Europe/Lisbon", label: "(GMT+0:00/+1:00) Lisbon", offset: 0, dstOffset: 1 },
  { value: "Europe/Paris", label: "(GMT+1:00/+2:00) Paris", offset: 1, dstOffset: 2 },
  { value: "Europe/Berlin", label: "(GMT+1:00/+2:00) Berlin", offset: 1, dstOffset: 2 },
  { value: "Europe/Rome", label: "(GMT+1:00/+2:00) Rome", offset: 1, dstOffset: 2 },
  { value: "Europe/Madrid", label: "(GMT+1:00/+2:00) Madrid", offset: 1, dstOffset: 2 },
  { value: "Europe/Amsterdam", label: "(GMT+1:00/+2:00) Amsterdam", offset: 1, dstOffset: 2 },
  { value: "Europe/Brussels", label: "(GMT+1:00/+2:00) Brussels", offset: 1, dstOffset: 2 },
  { value: "Europe/Vienna", label: "(GMT+1:00/+2:00) Vienna", offset: 1, dstOffset: 2 },
  { value: "Europe/Zurich", label: "(GMT+1:00/+2:00) Zurich", offset: 1, dstOffset: 2 },
  { value: "Europe/Stockholm", label: "(GMT+1:00/+2:00) Stockholm", offset: 1, dstOffset: 2 },
  { value: "Europe/Oslo", label: "(GMT+1:00/+2:00) Oslo", offset: 1, dstOffset: 2 },
  { value: "Europe/Copenhagen", label: "(GMT+1:00/+2:00) Copenhagen", offset: 1, dstOffset: 2 },
  { value: "Europe/Warsaw", label: "(GMT+1:00/+2:00) Warsaw", offset: 1, dstOffset: 2 },
  { value: "Europe/Prague", label: "(GMT+1:00/+2:00) Prague", offset: 1, dstOffset: 2 },
  { value: "Europe/Budapest", label: "(GMT+1:00/+2:00) Budapest", offset: 1, dstOffset: 2 },
  { value: "Europe/Bucharest", label: "(GMT+2:00/+3:00) Bucharest", offset: 2, dstOffset: 3 },
  { value: "Europe/Sofia", label: "(GMT+2:00/+3:00) Sofia", offset: 2, dstOffset: 3 },
  { value: "Europe/Athens", label: "(GMT+2:00/+3:00) Athens", offset: 2, dstOffset: 3 },
  { value: "Europe/Helsinki", label: "(GMT+2:00/+3:00) Helsinki", offset: 2, dstOffset: 3 },
  { value: "Europe/Riga", label: "(GMT+2:00/+3:00) Riga", offset: 2, dstOffset: 3 },
  { value: "Europe/Tallinn", label: "(GMT+2:00/+3:00) Tallinn", offset: 2, dstOffset: 3 },
  { value: "Europe/Vilnius", label: "(GMT+2:00/+3:00) Vilnius", offset: 2, dstOffset: 3 },
  { value: "Europe/Kiev", label: "(GMT+2:00/+3:00) Kiev", offset: 2, dstOffset: 3 },
  { value: "Europe/Moscow", label: "(GMT+3:00) Moscow", offset: 3 },
  { value: "Europe/Istanbul", label: "(GMT+3:00) Istanbul", offset: 3 },
  
  // Asia
  { value: "Asia/Dubai", label: "(GMT+4:00) Dubai", offset: 4 },
  { value: "Asia/Karachi", label: "(GMT+5:00) Karachi", offset: 5 },
  { value: "Asia/Kolkata", label: "(GMT+5:30) Mumbai, New Delhi", offset: 5.5 },
  { value: "Asia/Dhaka", label: "(GMT+6:00) Dhaka", offset: 6 },
  { value: "Asia/Bangkok", label: "(GMT+7:00) Bangkok", offset: 7 },
  { value: "Asia/Jakarta", label: "(GMT+7:00) Jakarta", offset: 7 },
  { value: "Asia/Shanghai", label: "(GMT+8:00) Beijing, Shanghai", offset: 8 },
  { value: "Asia/Hong_Kong", label: "(GMT+8:00) Hong Kong", offset: 8 },
  { value: "Asia/Singapore", label: "(GMT+8:00) Singapore", offset: 8 },
  { value: "Asia/Taipei", label: "(GMT+8:00) Taipei", offset: 8 },
  { value: "Asia/Seoul", label: "(GMT+9:00) Seoul", offset: 9 },
  { value: "Asia/Tokyo", label: "(GMT+9:00) Tokyo", offset: 9 },
  { value: "Asia/Manila", label: "(GMT+8:00) Manila", offset: 8 },
  { value: "Asia/Kuala_Lumpur", label: "(GMT+8:00) Kuala Lumpur", offset: 8 },
  { value: "Asia/Ho_Chi_Minh", label: "(GMT+7:00) Ho Chi Minh City", offset: 7 },
  
  // Australia/Oceania
  { value: "Australia/Perth", label: "(GMT+8:00) Perth", offset: 8 },
  { value: "Australia/Adelaide", label: "(GMT+9:30/+10:30) Adelaide", offset: 9.5, dstOffset: 10.5 },
  { value: "Australia/Darwin", label: "(GMT+9:30) Darwin", offset: 9.5 },
  { value: "Australia/Brisbane", label: "(GMT+10:00) Brisbane", offset: 10 },
  { value: "Australia/Sydney", label: "(GMT+10:00/+11:00) Sydney", offset: 10, dstOffset: 11 },
  { value: "Australia/Melbourne", label: "(GMT+10:00/+11:00) Melbourne", offset: 10, dstOffset: 11 },
  { value: "Pacific/Auckland", label: "(GMT+12:00/+13:00) Auckland", offset: 12, dstOffset: 13 },
  { value: "Pacific/Fiji", label: "(GMT+12:00/+13:00) Fiji", offset: 12, dstOffset: 13 },
  
  // Africa
  { value: "Africa/Cairo", label: "(GMT+2:00) Cairo", offset: 2 },
  { value: "Africa/Johannesburg", label: "(GMT+2:00) Johannesburg", offset: 2 },
  { value: "Africa/Lagos", label: "(GMT+1:00) Lagos", offset: 1 },
  { value: "Africa/Casablanca", label: "(GMT+1:00) Casablanca", offset: 1 },
  { value: "Africa/Nairobi", label: "(GMT+3:00) Nairobi", offset: 3 },
  
  // Americas - North America
  { value: "America/New_York", label: "(GMT-5:00/-4:00) New York", offset: -5, dstOffset: -4 },
  { value: "America/Chicago", label: "(GMT-6:00/-5:00) Chicago", offset: -6, dstOffset: -5 },
  { value: "America/Denver", label: "(GMT-7:00/-6:00) Denver", offset: -7, dstOffset: -6 },
  { value: "America/Los_Angeles", label: "(GMT-8:00/-7:00) Los Angeles", offset: -8, dstOffset: -7 },
  { value: "America/Phoenix", label: "(GMT-7:00) Phoenix", offset: -7 },
  { value: "America/Anchorage", label: "(GMT-9:00/-8:00) Anchorage", offset: -9, dstOffset: -8 },
  { value: "America/Honolulu", label: "(GMT-10:00) Honolulu", offset: -10 },
  { value: "America/Toronto", label: "(GMT-5:00/-4:00) Toronto", offset: -5, dstOffset: -4 },
  { value: "America/Vancouver", label: "(GMT-8:00/-7:00) Vancouver", offset: -8, dstOffset: -7 },
  { value: "America/Mexico_City", label: "(GMT-6:00/-5:00) Mexico City", offset: -6, dstOffset: -5 },
  
  // Americas - South America
  { value: "America/Sao_Paulo", label: "(GMT-3:00/-2:00) São Paulo", offset: -3, dstOffset: -2 },
  { value: "America/Buenos_Aires", label: "(GMT-3:00) Buenos Aires", offset: -3 },
  { value: "America/Santiago", label: "(GMT-3:00/-4:00) Santiago", offset: -3, dstOffset: -4 },
  { value: "America/Lima", label: "(GMT-5:00) Lima", offset: -5 },
  { value: "America/Bogota", label: "(GMT-5:00) Bogotá", offset: -5 },
  { value: "America/Caracas", label: "(GMT-4:00) Caracas", offset: -4 },
  
  // Additional important timezones
  { value: "Atlantic/Azores", label: "(GMT-1:00/+0:00) Azores", offset: -1, dstOffset: 0 },
  { value: "Atlantic/Canary", label: "(GMT+0:00/+1:00) Canary Islands", offset: 0, dstOffset: 1 },
  { value: "Indian/Maldives", label: "(GMT+5:00) Maldives", offset: 5 },
  { value: "Indian/Mauritius", label: "(GMT+4:00) Mauritius", offset: 4 },
  { value: "Pacific/Honolulu", label: "(GMT-10:00) Hawaii", offset: -10 },
  { value: "Pacific/Tahiti", label: "(GMT-10:00) Tahiti", offset: -10 },
  { value: "Pacific/Guam", label: "(GMT+10:00) Guam", offset: 10 },
  { value: "Pacific/Samoa", label: "(GMT-11:00) Samoa", offset: -11 },
];

// Function to get current offset for a timezone (considering DST)
const getCurrentOffset = (timezone) => {
  const now = moment();
  const utcOffset = now.tz(timezone).utcOffset();
  return utcOffset / 60; // Convert minutes to hours
};

// Function to format offset string
const formatOffset = (offset) => {
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const hours = Math.floor(absOffset);
  const minutes = Math.round((absOffset - hours) * 60);
  
  if (minutes === 0) {
    return `${sign}${hours}:00`;
  } else {
    return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  }
};

// Function to generate dynamic label with current offset
const generateLabel = (tzData) => {
  const currentOffset = getCurrentOffset(tzData.value);
  const offsetStr = formatOffset(currentOffset);
  
  // Extract city/country from the original label
  const cityMatch = tzData.label.match(/\)\s*(.+)$/);
  const city = cityMatch ? cityMatch[1] : tzData.value;
  
  return `(GMT${offsetStr}) ${city}`;
};

const TimezoneSelect = ({ value, onChange, ...props }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Generate timezone options with current offsets
  const timezoneOptions = useMemo(() => {
    return TIMEZONE_DATA.map(tzData => ({
      value: tzData.value,
      label: generateLabel(tzData),
      offset: getCurrentOffset(tzData.value),
      originalData: tzData
    }));
  }, []);

  // Filter timezones based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return timezoneOptions;
    
    const term = searchTerm.toLowerCase();
    return timezoneOptions.filter(option => 
      option.label.toLowerCase().includes(term) ||
      option.value.toLowerCase().includes(term)
    );
  }, [timezoneOptions, searchTerm]);

  // Find the current selected option
  const selectedOption = timezoneOptions.find(option => 
    option.value === value?.value || option.value === value
  );

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = (event) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleOptionSelect = (option) => {
    if (onChange) {
      onChange(option);
    }
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClickAway = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div>
        <TextField
          variant="outlined"
          placeholder="Search timezone..."
          value={selectedOption ? selectedOption.label : searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          fullWidth
          {...props}
        />
        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorEl ? anchorEl.offsetWidth : 'auto' }}
        >
          <Paper style={{ maxHeight: 300, overflow: 'auto' }}>
            <List dense>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <ListItem
                    key={option.value}
                    button
                    onClick={() => handleOptionSelect(option)}
                    style={{
                      backgroundColor: selectedOption?.value === option.value ? '#f5f5f5' : 'transparent'
                    }}
                  >
                    <ListItemText
                      primary={option.label}
                      secondary={option.value}
                      primaryTypographyProps={{ style: { fontWeight: 'bold' } }}
                      secondaryTypographyProps={{ style: { fontSize: '0.8em', color: '#666' } }}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No timezones found" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

TimezoneSelect.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
};

TimezoneSelect.defaultProps = {
  value: null,
};

export default TimezoneSelect;
